import React, { ReactElement, useEffect, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import Config from '../../../../../Config';
import type ProblemDomainSelectionBlockData from '../../../../../Models/Content/ProblemDomainSelectionBlockData';
import type { SubscriptionOption } from '../SubscriptionSelectionPage/SubscriptionSelectionPage';
import { FAULT_TYPE, resolveDomainRowFromCms, DetectFaultResult } from '../../problemDomainHelpers';
import { radarAnimation, iconBackChevron } from '../../images/index';
import '../DomainSelectionPage/domainSelectionPage.scss';
import './scanningPage.scss';
import { reportDWH } from '../../problemDomainBlockSlice';
import { useDispatch, useSelector } from 'react-redux';

const SCANNING_BODY_CLASS = 'problem-domain-scanning-active';

type CmsImageRef = {
  expandedValue?: { url?: string };
  value?: { url?: string };
};

type ProblemDomainItem = {
  productWorld?: { value?: unknown } | unknown;
  contentLink?: { id?: number | string };
  scanningIcon?: CmsImageRef;
};

type HeaderTab = {
  tabIconMobile?: CmsImageRef;
  tabIconDesktop?: CmsImageRef;
};

const mediaUrl = (imageProp: CmsImageRef | undefined): string | null => {
  const path = imageProp?.expandedValue?.url || imageProp?.value?.url;
  return path ? Config.epiBaseUrl + path : null;
};

export interface ScanningPageProps {
  iContent: ProblemDomainSelectionBlockData;
  productWorld: number;
  domainContentLinkId?: number | string | null;
  selectedSubscription: SubscriptionOption;
  onBack: () => void;
  onIdentifiedFault: (result: DetectFaultResult) => void;
  onUnidentifiedFault: () => void;
  detectFault: (
    subscription: SubscriptionOption,
    productWorld: number,
  ) => Promise<DetectFaultResult>;
}

const ScanningPage = (props: ScanningPageProps): ReactElement => {
  const dispatch = useDispatch();
  const { tabs, tabIndex } = useSelector((state: { Header?: { tabs?: HeaderTab[]; tabIndex?: number } }) => ({
    tabs: state.Header?.tabs,
    tabIndex: state.Header?.tabIndex ?? -1,
  }));
  const {
    iContent,
    productWorld,
    domainContentLinkId,
    selectedSubscription,
    onBack,
    onIdentifiedFault,
    onUnidentifiedFault,
    detectFault,
  } = props;

  useEffect(() => {
    document.body.classList.add(SCANNING_BODY_CLASS);

    return () => {
      document.body.classList.remove(SCANNING_BODY_CLASS);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const runFaultDetection = async () => {
      try {
        const result = await detectFault(selectedSubscription, productWorld);

        setTimeout(() => {
          if (cancelled) {
            return;
          }

          if (result.type === FAULT_TYPE.zone) {
            onIdentifiedFault(result);
            return;
          }

          if (result.type === FAULT_TYPE.powerSupply) {
            onIdentifiedFault(result);
            return;
          }

          onUnidentifiedFault();
        }, 3500);
      } catch (error) {
        dispatch(reportDWH({
          actionType: 'MalfunctionSearchFailed',
          status: 'Error',
          messageText: 'General Error',
        }));
        setTimeout(() => {
          if (!cancelled) {
            onUnidentifiedFault();
          }
        }, 3500);
      }
    };

    runFaultDetection();

    return () => {
      cancelled = true;
    };
  }, [dispatch, productWorld, selectedSubscription, detectFault, onIdentifiedFault, onUnidentifiedFault]);

  const domainRow = useMemo(() => {
    const domainItems = (iContent?.domainItems?.expandedValue || []) as unknown as ProblemDomainItem[];
    return resolveDomainRowFromCms(domainItems, productWorld, domainContentLinkId);
  }, [iContent?.domainItems?.expandedValue, productWorld, domainContentLinkId]);

  const cmsCenterIconUrl = mediaUrl(domainRow?.scanningIcon);

  const bgDesktop = mediaUrl(iContent?.backgroundImageDesktop as unknown as CmsImageRef);
  const bgMobile = mediaUrl(iContent?.backgroundImageMobile as unknown as CmsImageRef);

  const sectionStyle = {
    ...(bgDesktop ? { '--prob-domain-bg-desktop': `url("${bgDesktop}")` } : {}),
    ...(bgMobile ? { '--prob-domain-bg-mobile': `url("${bgMobile}")` } : {}),
  } as React.CSSProperties;

  const currentTab = tabIndex >= 0 ? tabs?.[tabIndex] : undefined;
  const logoIcon = isMobile ? currentTab?.tabIconMobile : currentTab?.tabIconDesktop;
  const logoUrl = mediaUrl(logoIcon);

  return (
    <section className="problem-domain-selection problem-domain-scanning" style={sectionStyle}>
      <header className="problem-domain-scanning__site-header-wrap">
        <div className="problem-domain-scanning__site-header">
          <span className="problem-domain-scanning__site-header-spacer" aria-hidden />

          <div className="problem-domain-scanning__site-header-logo">
            {logoUrl ? (
              <img src={logoUrl} alt="סלקום" className="problem-domain-scanning__site-header-logo-img" />
            ) : null}
          </div>

          <button
            type="button"
            className="problem-domain-scanning__site-header-back"
            onClick={onBack}
            aria-label="חזרה"
          >
            <img
              src={iconBackChevron}
              alt=""
              width={16}
              height={16}
              className="problem-domain-scanning__back-icon"
              aria-hidden
            />
          </button>
        </div>
      </header>

      <div className="problem-domain-scanning__shell">
        <div className="problem-domain-scanning__hero">
          <div className="problem-domain-scanning__radar" aria-hidden>
            <div className="problem-domain-scanning__svg-wrap">
              <img src={radarAnimation} alt="" className="problem-domain-scanning__radar-svg" />
            </div>

            <div
              className={
                cmsCenterIconUrl
                  ? 'problem-domain-scanning__radar-hub problem-domain-scanning__radar-hub--with-icon'
                  : 'problem-domain-scanning__radar-hub'
              }
            >
              {cmsCenterIconUrl ? (
                <img src={cmsCenterIconUrl} alt="" className="problem-domain-scanning__radar-hub-img" />
              ) : null}
            </div>
          </div>

          <p className="problem-domain-scanning__message">
            <span className="problem-domain-scanning__message-line">אנחנו סורקים את המערכות</span>
            <span className="problem-domain-scanning__message-line">לאיתור התקלה</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ScanningPage;
